import { isEqual, sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
// Custom hook.
// -----------------------------------------------------------------------------
export const useControlledTable = ({ actions, columns, data, onSelectedDataChange, selectedData }) => {
    // Hooks.
    // ---------------------------------------------------------------------------
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [sortColumn, setSortColumn] = useState();
    const [sortOrder, setSortOrder] = useState();
    // Variables.
    // ---------------------------------------------------------------------------
    const hasActions = useMemo(() => {
        if (!actions) {
            return false;
        }
        return actions.some((action) => {
            return rows.some((row) => {
                if (action.show === undefined) {
                    return true;
                }
                return action.show(row.original);
            });
        });
    }, [actions, rows]);
    // Funciones.
    // ---------------------------------------------------------------------------
    const handleOnChangeAll = useCallback(() => {
        if (onSelectedDataChange) {
            const everySelected = rows.every((row) => row.isSelected);
            const selectedRows = everySelected
                ? []
                : rows.map((row) => row.original);
            onSelectedDataChange(selectedRows);
        }
    }, [onSelectedDataChange, rows]);
    const handleOnChangeOne = useCallback((row) => {
        if (onSelectedDataChange) {
            const newState = !row.isSelected;
            const currentSelected = rows
                .filter((row) => row.isSelected)
                .map((row) => row.original);
            const selectedRows = newState
                ? [...currentSelected, row.original]
                : currentSelected.filter((current) => !isEqual(current, row.original));
            onSelectedDataChange(selectedRows);
        }
    }, [onSelectedDataChange, rows]);
    const handleOnSort = useCallback((label) => {
        // Sort master.
        const newSortColumn = columns.find((column) => column.label === label);
        // New order.
        const newSortOrder = sortColumn === undefined ||
            sortOrder === undefined ||
            sortColumn.label !== label
            ? 'asc'
            : sortOrder === 'asc'
                ? 'desc'
                : 'asc';
        // Sorted rows.
        const index = columns.findIndex((column) => column.label === label);
        const { sortValue, type } = columns[index];
        const full = rows.map((row) => {
            let sort;
            // Sort function.
            if (sortValue) {
                sort = sortValue(row.original);
            }
            else if (type === 'text') {
                sort = row.columns[index].toLowerCase();
            }
            else {
                sort = row.columns[index];
            }
            return {
                ...row,
                sort
            };
        });
        const partialNewRows = sortBy(full, 'sort');
        const newRows = newSortOrder === 'asc' ? partialNewRows : partialNewRows.reverse();
        // Update state.
        setSortColumn(newSortColumn);
        setSortOrder(newSortOrder);
        setRows(newRows);
    }, [columns, rows, sortColumn, sortOrder]);
    const handleSetRows = useCallback(() => {
        if (!data || data.length === 0) {
            setRows([]);
        }
        else {
            const items = data.map((original) => {
                const isSelected = selectedData.some((selected) => isEqual(selected, original));
                return {
                    columns,
                    isSelected,
                    original
                };
            });
            setRows(items);
        }
    }, [columns, data, selectedData]);
    const isSortColumn = useCallback((label) => {
        if (!sortColumn) {
            return false;
        }
        return sortColumn.label === label;
    }, [sortColumn]);
    // Effects.
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (isLoading) {
            setIsLoading(false);
            handleSetRows();
        }
    }, [handleSetRows, isLoading]);
    useEffect(() => {
        handleSetRows();
    }, [data, handleSetRows]);
    // Return.
    // ---------------------------------------------------------------------------
    return {
        hasActions,
        isLoading,
        isSortColumn,
        onChangeAll: handleOnChangeAll,
        onChangeOne: handleOnChangeOne,
        onSort: handleOnSort,
        rows,
        sortOrder
    };
};
//# sourceMappingURL=ControlledTable.hook.js.map